import { useEffect, useState } from "react";
import {
	Box,
	CircularProgress,
	Link,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { FormattedMessage } from "react-intl";
import { Link as RouterLink, useLocation } from "react-router-dom";

import { ApplicationWordArt, AuthenticationContainer, StyledLoginFormContainer } from "../../components";
import { useIdentityClient } from "../../hooks";
import { validateSaml } from "../../api/validate-saml";
import { type SamlValidationResult } from "../../schemas/saml-validation";

type ValidationState =
	| { status: "loading" }
	| { status: "success"; result: SamlValidationResult }
	| { status: "error" };

export const LoginSsoTroubleshoot = () => {
	const { client } = useIdentityClient();
	const location = useLocation();
	const token = new URLSearchParams(location.hash.slice(1)).get("idToken");
	const [validation, setValidation] = useState<ValidationState>({ status: "loading" });

	useEffect(() => {
		if (!token) {
			setValidation({ status: "error" });
			return;
		}

		let active = true;
		void validateSaml(client.getSdkRootUrl(), token)
			.then((result) => {
				if (active) {
					setValidation({ status: "success", result });
				}
			})
			.catch(() => {
				if (active) {
					setValidation({ status: "error" });
				}
			});

		return () => {
			active = false;
		};
	}, [client, token]);

	const backLink = (
		<Link
			component={RouterLink}
			to="/login"
			sx={{ alignSelf: "flex-end" }}
			data-testid="login-sso-troubleshoot__link__back"
		>
			<FormattedMessage id="login-sso-error.back" defaultMessage="Back to login" />
		</Link>
	);

	return (
		<AuthenticationContainer>
			<StyledLoginFormContainer maxWidth="md">
				<Stack spacing={4}>
					<ApplicationWordArt width={300} />
					<Paper variant="outlined" sx={{ p: 3, backgroundColor: "background.paper" }}>
						<Stack spacing={2}>
							<Typography
								variant="h1"
								sx={{ fontSize: "1.5rem" }}
								fontWeight="bold"
								data-testid="login-sso-troubleshoot__text__header"
							>
								<FormattedMessage id="login-sso-troubleshoot.header" defaultMessage="Advanced SSO Troubleshooting" />
							</Typography>
							{validation.status === "loading" && (
								<Box role="status" data-testid="login-sso-troubleshoot__status__loading">
									<CircularProgress />
									<Typography>
										<FormattedMessage id="login-sso-troubleshoot.loading" defaultMessage="Validating SSO details" />
									</Typography>
								</Box>
							)}
							{validation.status === "error" && (
								<Typography role="alert" data-testid="login-sso-troubleshoot__text__error">
									<FormattedMessage
										id="login-sso-troubleshoot.unavailable"
										defaultMessage="Troubleshooting details are unavailable. Please contact support."
									/>
								</Typography>
							)}
							{validation.status === "success" && <ValidationDetails result={validation.result} />}
							{backLink}
						</Stack>
					</Paper>
				</Stack>
			</StyledLoginFormContainer>
		</AuthenticationContainer>
	);
};

interface ValidationDetailsProps {
	readonly result: SamlValidationResult;
}

const ValidationDetails = ({ result }: ValidationDetailsProps) => {
	const claims = result.claims;
	const fields = result.error?.fields ?? [];
	const rows = [
		{
			field: "name",
			label: <FormattedMessage id="login-sso-troubleshoot.email" defaultMessage="Username / Email" />,
			value: claims?.name,
		},
		{
			field: "firstName",
			label: <FormattedMessage id="login-sso-troubleshoot.first-name" defaultMessage="First name" />,
			value: claims?.firstName,
		},
		{
			field: "lastName",
			label: <FormattedMessage id="login-sso-troubleshoot.last-name" defaultMessage="Last name" />,
			value: claims?.lastName,
		},
		{
			field: "primarySite",
			label: <FormattedMessage id="login-sso-troubleshoot.primary-site" defaultMessage="Primary site" />,
			value: claims?.primarySite,
		},
		{
			field: "additionalSites",
			label: <FormattedMessage id="login-sso-troubleshoot.additional-sites" defaultMessage="Additional sites" />,
			value: claims?.additionalSites.join(", "),
		},
		{
			field: "providedRoles/providedRole",
			label: <FormattedMessage id="login-sso-troubleshoot.roles" defaultMessage="Roles" />,
			value: claims?.providedRoles.join(", "),
		},
	];
	const extraFields = fields.filter((field) => claims === null || !rows.some((row) => row.field === field.field));

	return (
		<Stack spacing={2}>
			<Typography fontWeight="bold" data-testid="login-sso-troubleshoot__text__result">
				{result.success ? (
					<FormattedMessage
						id="login-sso-troubleshoot.passed"
						defaultMessage="SSO claim validation passed, but sign-in did not complete."
					/>
				) : fields.length === 0 ? (
					<FormattedMessage
						id="login-sso-troubleshoot.unavailable"
						defaultMessage="Troubleshooting details are unavailable. Please contact support."
					/>
				) : (
					<FormattedMessage id="login-sso-troubleshoot.failed" defaultMessage="SSO validation found issues" />
				)}
			</Typography>
			{claims?.name && (
				<Typography fontWeight="bold" data-testid="login-sso-troubleshoot__text__username">
					<FormattedMessage
						id="login-sso-troubleshoot.username"
						defaultMessage="Username: {username}"
						values={{ username: claims.name }}
					/>
				</Typography>
			)}
			{(claims !== null || fields.length > 0) && (
				<TableContainer>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>
									<FormattedMessage id="login-sso-troubleshoot.claim" defaultMessage="Claim" />
								</TableCell>
								<TableCell>
									<FormattedMessage id="login-sso-troubleshoot.received" defaultMessage="Value received" />
								</TableCell>
								<TableCell>
									<FormattedMessage id="login-sso-troubleshoot.issue" defaultMessage="Issue" />
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{claims &&
								rows.map((row) => (
									<TableRow key={row.field}>
										<TableCell>{row.label}</TableCell>
										<TableCell>
											{row.value || (
												<FormattedMessage id="login-sso-troubleshoot.no-value" defaultMessage="Not provided" />
											)}
										</TableCell>
										<TableCell>
											<Stack spacing={1}>
												{fields
													.filter((field) => field.field === row.field)
													.map((field) => (
														<SamlIssue key={field.issue} issue={field.issue} />
													))}
											</Stack>
										</TableCell>
									</TableRow>
								))}
							{extraFields.map((field) => (
								<TableRow key={field.field}>
									<TableCell>{field.field}</TableCell>
									<TableCell>
										{field.received || (
											<FormattedMessage id="login-sso-troubleshoot.no-value" defaultMessage="Not provided" />
										)}
									</TableCell>
									<TableCell>
										<SamlIssue issue={field.issue} />
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</Stack>
	);
};

interface SamlIssueProps {
	readonly issue: string;
}

const SamlIssue = ({ issue }: SamlIssueProps) => {
	switch (issue) {
		case "Missing":
			return <FormattedMessage id="login-sso-troubleshoot.missing" defaultMessage="Missing" />;
		case "Empty":
			return <FormattedMessage id="login-sso-troubleshoot.empty" defaultMessage="Empty" />;
		case "Invalid":
			return <FormattedMessage id="login-sso-troubleshoot.invalid" defaultMessage="Invalid" />;
		case "NoMatch":
			return <FormattedMessage id="login-sso-troubleshoot.no-match" defaultMessage="Not configured in PackNet" />;
		default:
			return <FormattedMessage id="login-sso-troubleshoot.unknown-issue" defaultMessage="Validation issue" />;
	}
};
